import {
  doc,
  getDoc,
  collection,
  updateDoc,
  onSnapshot,
  where,
  query,
  getDocs,
} from 'firebase/firestore';
import { User as FirebaseAuthUser, getIdTokenResult } from 'firebase/auth';
import { auth, db } from '@/firebaseConfig';
import { createStore } from 'vuex';
import { retrieveProducts, retrieveSubscriptions } from '@/utils/StripeService';
import { Subscription } from '@stripe/firestore-stripe-payments';
import {
  Keyword,
  Content,
  UserData,
  Sprint,
  Location,
} from '@/constants/types';

import { DEMO_KEYWORD, DEMO_SPRINT, DEMO_DOCUMENTS } from '@/constants/demo';
import userModule from './modules/user';
import toastModule from './modules/toast';

// const getDefaultState = () => ({
//   redirect: false,
//   isAuthenticated: false,
//   onboardingComplete: false,
//   isAdmin: false,
//   hasActiveSubscription: false,
//   user: null as FirebaseAuthUser | null,
//   userData: null as UserData | null,
//   keywords: [] as Keyword[],
//   sprints: [],
//   documents: [] as Content[],
//   products: [],
//   portalLink: '',
//   subscriptions: [] as Subscription[],
//   unsubscribe: [] as any[],
//   alert: undefined,
// });

const store = createStore({
  state: {
    currentUser: null as FirebaseAuthUser | null,
    userData: null as UserData | null,
    unsubscribe: [] as any[],
    alert: undefined,
    redirect: false,
    products: [] as any[],
    currentPlan: null as any,
    locations: [] as Location[],
    currentLocation: null as Location | null,
    keywords: [] as Keyword[],
    sprints: [] as Sprint[],
    documents: [] as Content[],
  },
  getters: {
    getAlert: (state) => state.alert,
    getCurrentUser: (state) => state.currentUser,
    getUserData: (state) => state.userData,
    getRedirect: (state) => state.redirect,
    getProducts: (state) => state.products,
    getCurrentPlan: (state) => state.currentPlan,
    getCurrentLocation: (state) => state.currentLocation,
    getCurrentKeywords: (state) => state.keywords,
    getCurrentSprints: (state) => state.sprints,
    getCurrentDocuments: (state) => state.documents,
    getLocations: (state) => state.locations,
  },
  mutations: {
    setAlert(state, payload) {
      state.alert = payload;
    },
    setCurrentUser(state, payload) {
      state.currentUser = payload;
    },
    setUserData(state, payload) {
      state.userData = payload;
    },
    setRedirect(state, boolean) {
      state.redirect = boolean;
    },
    setProducts(state, payload) {
      state.products = payload;
    },
    setCurrentPlan(state, payload) {
      state.currentPlan = payload;
    },
    setCurrentLocation(state, payload) {
      state.currentLocation = payload;
    },
    setKeywords(state, payload) {
      state.keywords = payload;
    },
    setSprints(state, payload) {
      state.sprints = payload;
    },
    setDocuments(state, payload) {
      state.documents = payload;
    },
    setLocations(state, payload) {
      state.locations = payload;
    },
    setUnsubscribe(state, func) {
      state.unsubscribe = func;
    },
    updateDocumentStatus(state, { id, status }) {
      const document = state.documents.find((documentDoc) => documentDoc.id === id);
      if (document) {
        document.status = status;
      }
    },
    updateDocument(state, { id, payload }) {
      const document = state.documents.find((documentDoc) => documentDoc.id === id) as Content;
      if (document) {
        Object.assign(document, payload);
      }
    },

    // setProducts(state, products) {
    //   state.products = products;
    // },
    // RESET_STATE(state) {
    //   Object.assign(state, getDefaultState());
    // },
  },
  actions: {
    updateCurrentUser({ commit }, payload) {
      commit('setCurrentUser', payload);
    },
    updateUserData({ commit }, payload) {
      commit('setUserData', payload);
    },
    updateRedirect({ commit }, payload) {
      commit('setRedirect', payload);
    },
    updateProducts({ commit }, payload) {
      commit('setProducts', payload);
    },
    updateCurrentPlan({ commit }, payload) {
      commit('setCurrentPlan', payload);
    },
    async fetchKeywordsForLocation({ commit }, locationId) {
      try {
        const keywordsCollection = collection(db, 'locations', locationId, 'keywords');
        const snapshot = await getDocs(keywordsCollection);
        const keywords = snapshot.docs.map((keyword) => ({ id: keyword.id, ...keyword.data() }));
        commit('setKeywords', keywords);
      } catch (error) {
        console.error('Error fetching keywords:', error);
      }
    },
    async fetchSprintsForLocation({ commit }, locationId) {
      try {
        const sprintsCollection = collection(db, 'locations', locationId, 'sprints');
        const snapshot = await getDocs(sprintsCollection);
        const sprints = snapshot.docs.map((sprint) => ({ id: sprint.id, ...sprint.data() }));
        commit('setSprints', sprints);
      } catch (error) {
        console.error('Error fetching sprints:', error);
      }
    },
    async fetchDocumentsForLocation({ commit }, locationId) {
      try {
        const documentsCollection = collection(db, 'locations', locationId, 'content');
        const snapshot = await getDocs(documentsCollection);
        const documents = snapshot.docs.map((document) => ({ id: document.id, ...document.data() }));
        commit('setDocuments', documents);
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    },
    updateCurrentLocation({ commit, dispatch }, payload) {
      commit('setCurrentLocation', payload);

      // Fetch new data based on the updated location
      dispatch('fetchKeywordsForLocation', payload.id);
      dispatch('fetchSprintsForLocation', payload.id);
      dispatch('fetchDocumentsForLocation', payload.id);
    },
    setCurrentLocationById({ commit, state }, locationId: string) {
      const location = state.locations.find((loc) => loc.id === locationId);
      if (location) {
        commit('setCurrentLocation', location);
      }
    },
    updateKeywords({ commit }, payload) {
      commit('setKeywords', payload);
    },
    updateSprints({ commit }, payload) {
      commit('setSprints', payload);
    },
    updateDocuments({ commit }, payload) {
      commit('setDocuments', payload);
    },
    updateLocations({ commit }, payload) {
      commit('setLocations', payload);
    },
    async updateDocumentStatus({ commit }, { id, sprintId, status }) {
      // Commit the mutation to update the local state
      commit('updateDocumentStatus', { id, sprintId, status });

      // Update the Firebase document
      try {
        const l = this.getters.getCurrentLocation;
        if (l) {
          const documentRef = doc(db, 'locations', l.id, 'content', id);
          await updateDoc(documentRef, { status });

          const sprintRef = doc(db, 'locations', l.id, 'sprints', sprintId);
          const sprintDoc = await getDoc(sprintRef);
          const sprint = sprintDoc.data();
          if (sprint) {
            sprint.content[id] = status;
            await updateDoc(sprintRef, { content: sprint.content });
          }
        }
      } catch (error) {
        console.error('Error updating document status:', error);
      }
    },
    async updateDocument({ commit }, { id, payload }) {
      commit('updateDocument', { id, payload });
      // Update the Firebase document
      try {
        const l = this.getters.getCurrentLocation;
        if (l) {
          const documentRef = doc(db, 'locations', l.id, 'content', id);
          await updateDoc(documentRef, payload);
        }
      } catch (error) {
        console.error('Error updating document status:', error);
      }
    },
    showAlert({ commit, dispatch }, payload) {
      dispatch('hideAlert');
      const timer = setTimeout(() => {
        dispatch('hideAlert');
      }, payload.duration || 2000);
      commit('setAlert', { ...payload, timer });
    },
    hideAlert({ commit, getters }) {
      if (getters.getAlert) {
        clearTimeout(getters.getAlert.timer);
        commit('setAlert', undefined);
      }
    },
  },
  modules: {
    user: userModule,
    toast: toastModule,
  },
});

export default store;
